import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { clearCart as clearCartRequest, resolveCart } from './cartApi'
import { writeCheckoutGuestCartId, writeStoredCartId } from './cartPersistence'
import {
  useCartBootstrap,
  useCartIdentitySync,
  useCartMutations,
} from './hooks'
import type { Cart } from './types'

type CartContextValue = {
  cart: Cart | null
  cartId: string | null
  itemCount: number
  subtotal: number
  total: number
  drawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
  toggleDrawer: () => void
  initializing: boolean
  busy: boolean
  error: string
  clearError: () => void
  getLastError: () => string
  refreshCart: () => Promise<void>
  addItem: (productId: string, quantity?: number) => Promise<boolean>
  updateItemQuantity: (itemId: string, quantity: number) => Promise<boolean>
  removeItem: (itemId: string) => Promise<boolean>
  clearCart: () => Promise<boolean>
  resetAfterCheckout: (consumedCartId: string) => Promise<void>
}

const CartContext = createContext<CartContextValue | null>(null)

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, setCart] = useState<Cart | null>(null)
  const [cartId, setCartId] = useState<string | null>(null)
  const [initializing, setInitializing] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const clearError = useCallback(() => setError(''), [])
  const openDrawer = useCallback(() => setDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])
  const toggleDrawer = useCallback(() => setDrawerOpen((current) => !current), [])

  const applyCart = useCallback((nextCart: Cart) => {
    setCart(nextCart)
    setCartId(nextCart.cartId)
    writeStoredCartId(nextCart.cartId)
  }, [])

  const resetAfterCheckout = useCallback(async (consumedCartId: string) => {
    try {
      await clearCartRequest(consumedCartId)
    } catch {
      // The orders service owns the authoritative cleanup. Repeating it from
      // the browser is intentionally idempotent.
    }

    if (cartId && cartId !== consumedCartId) return

    writeStoredCartId(null)
    writeCheckoutGuestCartId(null)
    setCart(null)
    setCartId(null)

    try {
      const freshCart = await resolveCart()
      applyCart(freshCart)
      setError('')
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Errore creazione nuovo carrello')
    }
  }, [applyCart, cartId])

  const {
    ensureCart,
    refreshCart,
    addItem,
    updateItemQuantity,
    removeItem,
    clearCart,
    getLastError,
  } = useCartMutations({
    cartId,
    applyCart,
    setBusy,
    setError,
  })

  useCartBootstrap({
    ensureCart,
    setInitializing,
    setError,
  })

  useCartIdentitySync({
    refreshCart,
  })

  const value = useMemo<CartContextValue>(
    () => ({
      cart,
      cartId,
      itemCount: cart?.itemCount ?? 0,
      subtotal: cart?.subtotal ?? 0,
      total: cart?.total ?? 0,
      drawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      initializing,
      busy,
      error,
      clearError,
      getLastError,
      refreshCart,
      addItem,
      updateItemQuantity,
      removeItem,
      clearCart,
      resetAfterCheckout,
    }),
    [
      addItem,
      busy,
      cart,
      cartId,
      clearCart,
      clearError,
      closeDrawer,
      drawerOpen,
      error,
      getLastError,
      initializing,
      openDrawer,
      refreshCart,
      removeItem,
      resetAfterCheckout,
      toggleDrawer,
      updateItemQuantity,
    ],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart deve essere usato dentro CartProvider')
  }
  return context
}
